import {
  afterNextRender,
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  signal,
  Signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { AsyncPipe, DOCUMENT } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Router,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { Directionality } from '@angular/cdk/bidi';
import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { OverlayContainer } from '@angular/cdk/overlay';
import { Observable } from 'rxjs';
import { UserModels, OrganizationModels } from '@share-models';
import { UiAvatarComponent } from '@ui';
import { AppState } from '../../store/app.state';
import { AuthActions } from '../../store/state/auth/auth.actions';
import { userFeature } from '../../store/state/user/user.feature';
import { organizationFeature } from '../../store/state/organization/organization.feature';
import {
  NAVIGATION_ITEMS,
  USER_NAVIGATION_ITEMS,
} from '../../consts/navigation.const';
import { PanicNavigationModels } from '../../models/navigation.model';
import {
  injectVisibleNavigationItems,
  isNavigationItemActive,
} from '../../helpers/navigation.helper';
import { MobileDrawerService } from './mobile-drawer.service';
import { MobileOrgSelectorComponent } from './components/mobile-org-selector/mobile-org-selector.component';
import { MobileBackButtonService } from '../../services/mobile-back-button.service';
import { MobileBackButtonModels } from '../../models/mobile-back-button.model';

const SWIPE_CLOSE_THRESHOLD = 80;
const SWIPE_ANGLE_LOCK_DEGREES = 30;

const USER_AVATAR_COLOR = 'var(--color-primary-500)';

@Component({
  selector: 'app-mobile-nav-drawer',
  templateUrl: './mobile-nav-drawer.component.html',
  styleUrl: './mobile-nav-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AsyncPipe,
    TranslatePipe,
    MatIconModule,
    UiAvatarComponent,
    MobileOrgSelectorComponent,
  ],
})
export class MobileNavDrawerComponent {
  private readonly router = inject(Router);
  private readonly store$ = inject(Store<AppState>);
  private readonly mobileDrawerService = inject(MobileDrawerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dir = inject(Directionality);
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(Injector);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly doc = inject(DOCUMENT);
  private readonly overlayContainer: OverlayContainer = inject(OverlayContainer);
  private readonly mobileBackButtonService = inject(MobileBackButtonService);

  private unregisterBackHandler: (() => void) | null = null;

  private readonly handleBack: MobileBackButtonModels.OverlayHandler = () => {
    this.close();

    return true;
  };

  public readonly drawerPanelRef =
    viewChild<ElementRef<HTMLElement>>('drawerPanel');
  public readonly drawerBackdropRef =
    viewChild<ElementRef<HTMLElement>>('drawerBackdrop');
  public readonly drawerPortalTpl = viewChild<TemplateRef<unknown>>(
    'drawerPortalContent',
  );

  private portalOutlet: DomPortalOutlet | null = null;

  public readonly isDrawerOpen = signal<boolean>(false);
  public readonly navigatingPath = signal<string | null>(null);

  public readonly navigationItems: Signal<
    readonly PanicNavigationModels.ResolvedItem[]
  > = injectVisibleNavigationItems(NAVIGATION_ITEMS);

  public readonly userItems: readonly PanicNavigationModels.ResolvedItem[] =
    USER_NAVIGATION_ITEMS;

  public readonly selectUser$: Observable<UserModels.UserDocument | null> =
    this.store$.select(userFeature.selectProfile);

  public readonly selectActiveOrganization$: Observable<OrganizationModels.OrganizationDocument | null> =
    this.store$.select(organizationFeature.selectActiveOrganization);

  public readonly isOrgSelectorOpen = signal<boolean>(false);
  protected readonly userAvatarColor: string = USER_AVATAR_COLOR;

  public readonly activeOrganization = toSignal(
    this.selectActiveOrganization$,
    { initialValue: null },
  );

  public readonly userOrganizations = toSignal(
    this.store$.select(organizationFeature.selectUserOrganizations),
    { initialValue: [] as OrganizationModels.UserOrganizationDropDown[] },
  );

  public get isRtl(): boolean {
    return this.dir.value === 'rtl';
  }

  private startX = 0;
  private startY = 0;
  private currentX = 0;
  private isDragging = false;
  private directionLocked = false;

  constructor() {
    this.mobileDrawerService.open$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.openDrawer());

    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.navigatingPath.set(null));

    afterNextRender(() => {
      const host = this.doc.createElement('div');
      host.classList.add('mobile-nav-drawer-portal');
      this.overlayContainer.getContainerElement().appendChild(host);
      this.portalOutlet = new DomPortalOutlet(
        host,
        undefined,
        this.appRef,
        this.injector,
      );
      const tpl = this.drawerPortalTpl();
      if (tpl) {
        this.portalOutlet.attach(
          new TemplatePortal(tpl, this.viewContainerRef),
        );
      }
    });

    this.destroyRef.onDestroy(() => {
      this.doc.body.style.overflow = '';
      this.portalOutlet?.detach();
      this.portalOutlet?.outletElement?.remove();
    });

    effect((onCleanup) => {
      const panel = this.drawerPanelRef()?.nativeElement;
      const backdrop = this.drawerBackdropRef()?.nativeElement;
      if (!panel) return;

      const onStart = (e: TouchEvent): void => this.onTouchStart(e);
      const onMove = (e: TouchEvent): void => this.onTouchMove(e);
      const onEnd = (): void => this.onTouchEnd();

      const targets = backdrop ? [panel, backdrop] : [panel];
      for (const el of targets) {
        el.addEventListener('touchstart', onStart, { passive: true });
        el.addEventListener('touchmove', onMove, { passive: true });
        el.addEventListener('touchend', onEnd, { passive: true });
      }

      onCleanup(() => {
        for (const el of targets) {
          el.removeEventListener('touchstart', onStart);
          el.removeEventListener('touchmove', onMove);
          el.removeEventListener('touchend', onEnd);
        }
      });
    });
  }

  public openDrawer(): void {
    this.isDrawerOpen.set(true);
    this.doc.body.style.overflow = 'hidden';
    this.unregisterBackHandler ??= this.mobileBackButtonService.registerOverlay(
      this.handleBack,
    );
  }

  public close(): void {
    if (!this.isDrawerOpen()) return;
    this.isDrawerOpen.set(false);
    this.doc.body.style.overflow = '';
    this.unregisterBackHandler?.();
    this.unregisterBackHandler = null;
  }

  public isActiveRoute(item: PanicNavigationModels.ResolvedItem): boolean {
    return isNavigationItemActive(this.router, item);
  }

  public navigateTo(item: PanicNavigationModels.ResolvedItem): void {
    const url = item.path;
    if (this.router.url === url) {
      this.close();
      return;
    }
    this.navigatingPath.set(url);

    const panel = this.drawerPanelRef()?.nativeElement;
    const backdrop = this.drawerBackdropRef()?.nativeElement;
    const hideX = this.isRtl ? '100%' : '-100%';
    if (panel) {
      panel.style.transition = 'none';
      panel.style.transform = `translateX(${hideX})`;
    }
    if (backdrop) {
      backdrop.style.transition = 'none';
      backdrop.style.opacity = '0';
    }

    this.close();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        void this.router.navigateByUrl(url);
        if (panel) {
          panel.style.transition = '';
          panel.style.transform = '';
        }
        if (backdrop) {
          backdrop.style.transition = '';
          backdrop.style.opacity = '';
        }
      });
    });
  }

  public logout(): void {
    this.close();
    this.store$.dispatch(AuthActions.logout({ msg: null }));
  }

  public openOrgSelector(): void {
    this.isOrgSelectorOpen.set(true);
  }

  public closeOrgSelector(): void {
    this.isOrgSelectorOpen.set(false);
  }

  private onTouchStart(event: TouchEvent): void {
    this.startX = event.touches[0].clientX;
    this.startY = event.touches[0].clientY;
    this.currentX = 0;
    this.isDragging = false;
    this.directionLocked = false;
  }

  private onTouchMove(event: TouchEvent): void {
    const dx = event.touches[0].clientX - this.startX;
    const dy = event.touches[0].clientY - this.startY;

    if (!this.directionLocked) {
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      if (absDx < 5 && absDy < 5) return;

      const angle = Math.atan2(absDy, absDx) * (180 / Math.PI);
      if (angle > SWIPE_ANGLE_LOCK_DEGREES) {
        this.directionLocked = true;
        this.isDragging = false;
        return;
      }
      this.directionLocked = true;
      this.isDragging = true;
    }

    if (!this.isDragging) return;

    const isClosingDrag = this.isRtl ? dx > 0 : dx < 0;
    if (!isClosingDrag) {
      this.currentX = 0;
      this.applyTransform(0);
      return;
    }

    this.currentX = dx;
    this.applyTransform(dx);
  }

  private onTouchEnd(): void {
    if (!this.isDragging) return;

    if (Math.abs(this.currentX) > SWIPE_CLOSE_THRESHOLD) {
      this.swipeClose();
    } else {
      this.snapBack();
    }

    this.isDragging = false;
    this.directionLocked = false;
  }

  private applyTransform(dx: number): void {
    const el = this.drawerPanelRef()?.nativeElement;
    if (!el) return;
    el.style.transform = `translateX(${dx}px)`;
    el.style.transition = 'none';

    const backdrop = this.drawerBackdropRef()?.nativeElement;
    if (backdrop) {
      const ratio = 1 - Math.abs(dx) / el.offsetWidth;
      backdrop.style.opacity = `${Math.max(0, ratio)}`;
      backdrop.style.transition = 'none';
    }
  }

  private swipeClose(): void {
    const el = this.drawerPanelRef()?.nativeElement;
    if (!el) return;

    const hideX = this.isRtl ? '100%' : '-100%';
    el.style.transition = 'transform 0.2s cubic-bezier(0.2, 0, 0, 1)';
    el.style.transform = `translateX(${hideX})`;

    const backdrop = this.drawerBackdropRef()?.nativeElement;
    if (backdrop) {
      backdrop.style.transition = 'opacity 0.2s cubic-bezier(0.2, 0, 0, 1)';
      backdrop.style.opacity = '0';
    }

    el.addEventListener(
      'transitionend',
      () => {
        this.close();
        el.style.transition = '';
        el.style.transform = '';
        if (backdrop) {
          backdrop.style.transition = '';
          backdrop.style.opacity = '';
        }
      },
      { once: true },
    );
  }

  private snapBack(): void {
    const el = this.drawerPanelRef()?.nativeElement;
    if (!el) return;

    el.style.transition = 'transform 0.2s cubic-bezier(0.2, 0, 0, 1)';
    el.style.transform = 'translateX(0)';

    const backdrop = this.drawerBackdropRef()?.nativeElement;
    if (backdrop) {
      backdrop.style.transition = 'opacity 0.2s cubic-bezier(0.2, 0, 0, 1)';
      backdrop.style.opacity = '1';
    }

    el.addEventListener(
      'transitionend',
      () => {
        el.style.transition = '';
        el.style.transform = '';
        if (backdrop) {
          backdrop.style.transition = '';
          backdrop.style.opacity = '';
        }
      },
      { once: true },
    );
  }
}
